import React, { useEffect, useRef, useState } from 'react';
import styles from './EditableSidePane.module.css';
import Button from '../Button/Button';
import EditableTextField from '../EditableTextField/EditableTextField';
import LockIndicator from '../LockIndicator/LockIndicator';
import { toast } from 'react-toastify';
import { beginPendingSave, useUnsavedChangesWarning } from '../../../Hooks/useUnsavedChangesWarning';

export type EditablePaneCtx<T extends object> = {
  isEditing: boolean;
  data: T; // data = isEditing ? draft : entity
  draft: T;
  setDraft: React.Dispatch<React.SetStateAction<T>>;
  updateDraft: <K extends keyof T>(key: K, value: T[K]) => void;
};

type EditableSidePaneProps<T extends object> = {
  entity: T;
  entityId: string;

  /** Called on autosave + on leaving edit mode when changes exist */
  onSave: (payload: T) => Promise<void>;

  /** Let the parent sync its state so view-mode reflects changes immediately */
  onUpdated?: (payload: T) => void;

  /** Debounce interval */
  saveDebounceMs?: number;

  /** Edit button text */
  editText?: string;
  doneText?: string;

  /** Should we show the details tile at all? */
  showDetails: boolean;

  /** Details tile contents */
  renderDetails: (ctx: EditablePaneCtx<T>) => React.ReactNode;

  /** Optional content below details (e.g. Tasks list) */
  footer?: React.ReactNode;

  /**
   * ✅ Built-in title support (edits draft, not some external state)
   * Example: titleKey="title" or titleKey="taskHeading"
   */
  titleKey?: keyof T;
  titleFallback?: string;

  /**
   * Optional header extras (badges, buttons, etc.)
   */
  renderHeaderRight?: (ctx: EditablePaneCtx<T>) => React.ReactNode;
  renderHeaderBelow?: (ctx: EditablePaneCtx<T>) => React.ReactNode;

  editMode: boolean;
  locked: boolean;
  lockedBy?: string;

  onContentClick?: (data: T) => void;
};

function EditableSidePane<T extends object>({
  entity,
  entityId,
  onSave,
  onUpdated,
  saveDebounceMs = 800,
  editText = 'Edit',
  doneText = 'Finished Editing',
  showDetails,
  renderDetails,
  footer,
  titleKey,
  titleFallback = 'Untitled',
  renderHeaderRight,
  renderHeaderBelow,
  editMode,
  locked,
  lockedBy,
  onContentClick
}: EditableSidePaneProps<T>) {
  const [isEditing, setIsEditing] = useState(editMode);
  const [draft, setDraft] = useState<T>(entity);
  const [lastSavedSerialized, setLastSavedSerialized] = useState(() => JSON.stringify(entity));

  const saveTimerRef = useRef<number | null>(null);
  const endPendingSaveRef = useRef<(() => void) | null>(null);

  // Keep edit mode in sync with external lock + editMode flags
  useEffect(() => {
    setIsEditing(!locked && editMode);
  }, [locked, editMode]);

  useEffect(() => {
    const serialized = JSON.stringify(entity);
    setLastSavedSerialized(serialized);
    setDraft(entity);
  }, [entity]);

  const updateDraft = <K extends keyof T>(key: K, value: T[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const data = isEditing ? draft : entity;

  const hasUnsavedDraft = isEditing && JSON.stringify(draft) !== lastSavedSerialized;

  useUnsavedChangesWarning(hasUnsavedDraft);

  // Autosave
  useEffect(() => {
    if (!isEditing) return;

    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);

    const serialized = JSON.stringify(draft);
    if (serialized === lastSavedSerialized) {
      endPendingSaveRef.current?.();
      endPendingSaveRef.current = null;
      return;
    }

    if (!endPendingSaveRef.current) {
      endPendingSaveRef.current = beginPendingSave();
    }

    saveTimerRef.current = window.setTimeout(async () => {
      try {
        await onSave(draft);
        setLastSavedSerialized(serialized);
        onUpdated?.(draft);
      } catch (err) {
        console.error('Autosave failed:', err);
      } finally {
        endPendingSaveRef.current?.();
        endPendingSaveRef.current = null;
      }
    }, saveDebounceMs);

    return () => {
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    };
  }, [draft, isEditing, onSave, onUpdated, saveDebounceMs, lastSavedSerialized]);

  useEffect(() => {
    if (isEditing) return;
    endPendingSaveRef.current?.();
    endPendingSaveRef.current = null;
  }, [isEditing]);

  useEffect(() => {
    return () => {
      endPendingSaveRef.current?.();
      endPendingSaveRef.current = null;
    };
  }, []);

  const flushSave = async () => {
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);

    const serialized = JSON.stringify(draft);
    if (serialized === lastSavedSerialized) return;

    await onSave(draft);
    setLastSavedSerialized(serialized);
    onUpdated?.(draft);
    endPendingSaveRef.current?.();
    endPendingSaveRef.current = null;
  };

  const blockIfLocked = (e: React.SyntheticEvent) => {
    onContentClick?.(draft);

    if (!locked) return;

    e.preventDefault();
    e.stopPropagation();

    const ne = e.nativeEvent as any;
    ne?.stopImmediatePropagation?.();

    toast.info('This item is being edited by another user');
  };

  const ctx: EditablePaneCtx<T> = { isEditing, data, draft, setDraft, updateDraft };

  const titleValue = titleKey ? (data[titleKey] as any) : '';
  const titleText = (titleValue ?? '').toString().trim() || titleFallback;

  return (
    <div className={styles.container}>
      {/* Header */}
      {titleKey && (
        <div className={styles.header} onClick={blockIfLocked}>
          <div className={styles.headerRow}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {isEditing ? (
                <EditableTextField
                  value={titleText}
                  onChange={(v) => updateDraft(titleKey, v as any)}
                  canEdit
                  placeholder=""
                  className={styles.titleInput}
                />
              ) : (
                <h2 className={styles.title}>{titleText}</h2>
              )}

              {locked && <LockIndicator lockedBy={lockedBy} />}
            </div>

            {renderHeaderRight?.(ctx)}
          </div>

          {renderHeaderBelow?.(ctx)}
        </div>
      )}


      
      <div className={styles.details} onClick={blockIfLocked}>

        {/* Edit button + details
        <Button
          size="Small"
          colour="#FFF"
          textColour="#000"
          border="rgba(0,0,0,0.2)"
          className={styles.editBtn}
          clickHandler={async () => {
            if (isEditing) {
              try {
                await flushSave();
              } catch (err) {
                console.error('Final save failed:', err);
              }
              setIsEditing(false);
            } else {
              // enter edit mode from latest entity, and baseline matches it
              setLastSavedSerialized(JSON.stringify(entity));
              setDraft(entity);
              setIsEditing(true);
            }
          }}
        >
          <span className={styles.editBtnText}>{isEditing ? doneText : editText}</span>
          <img
            className={`${styles.editBtnIcon} ${isEditing ? styles.hiddenIcon : ''}`}
            src="/img/edit.svg"
            alt="Edit"
          />
        </Button>*/}

        {showDetails && renderDetails(ctx)}
      </div> 

      {footer}
    </div>
  );
}

export default EditableSidePane;
